import React, { createContext, useContext, useMemo } from 'react';
import { createContextualCan } from '@casl/react';
import { createMongoAbility, AbilityBuilder, MongoAbility } from '@casl/ability';
import type { VendorPermission } from '@clareefai/types';

export type Subject =
  | 'TeamMember'
  | 'Reward'
  | 'Promoter'
  | 'Prospect'
  | 'Testimonial'
  | 'Review'
  | 'UseCase'
  | 'EmailTemplate'
  | 'Subscription'
  | 'Page'
  | 'CrmImport'
  | 'Balance'
  | 'FundRequest'
  | 'Settings'
  | 'Permission'
  | 'all';

export type Action = 'manage' | 'create' | 'read' | 'update' | 'delete' | 'access' | 'publish' | 'export';

export type AppAbility = MongoAbility<[Action, Subject]>;

const defaultAbility = createMongoAbility<[Action, Subject]>();

export const AbilityContext = createContext<AppAbility>(defaultAbility);

export const Can = createContextualCan(AbilityContext.Consumer);

export const useAbility = (): AppAbility => {
  return useContext(AbilityContext);
};

const permissionToAbility: Record<VendorPermission, { action: Action; subject: Subject }[]> = {
  change_email_template: [{ action: 'manage', subject: 'EmailTemplate' }],
  change_use_case_question: [{ action: 'update', subject: 'UseCase' }],
  change_review_question: [{ action: 'update', subject: 'Review' }],
  change_tone_of_voice_prompt: [{ action: 'update', subject: 'Page' }],
  create_modify_reward: [{ action: 'manage', subject: 'Reward' }],
  add_funds_reward: [{ action: 'manage', subject: 'Balance' }, { action: 'manage', subject: 'FundRequest' }],
  request_add_funds: [{ action: 'read', subject: 'Balance' }, { action: 'create', subject: 'FundRequest' }],
  add_another_admin: [{ action: 'create', subject: 'TeamMember' }],
  assign_licenses: [{ action: 'update', subject: 'TeamMember' }],
  connect_integrate_crm: [{ action: 'access', subject: 'CrmImport' }],
  switch_page_draft_publish: [{ action: 'update', subject: 'Page' }],
  view_manage_subscription: [{ action: 'manage', subject: 'Subscription' }],
  edit_public_page: [{ action: 'update', subject: 'Page' }],
  absorb_external_review: [{ action: 'create', subject: 'Review' }],
  create_customer_reward: [{ action: 'create', subject: 'Reward' }],
  publish_review_testimonial: [{ action: 'publish', subject: 'Testimonial' }, { action: 'publish', subject: 'Review' }],
  export_review_testimonial: [{ action: 'export', subject: 'Testimonial' }, { action: 'export', subject: 'Review' }],
  create_team_incentive: [{ action: 'create', subject: 'Reward' }],
  view_all_promoters: [{ action: 'read', subject: 'Promoter' }],
  view_team_activity: [{ action: 'read', subject: 'TeamMember' }],
  filter_per_end_user: [{ action: 'access', subject: 'TeamMember' }],
  upload_assign_promoters_to_csm: [{ action: 'update', subject: 'TeamMember' }],
  view_sales_reps_activity: [{ action: 'access', subject: 'TeamMember' }],
  check_progress_1to1_call: [{ action: 'access', subject: 'TeamMember' }],
  create_reporting_subscribe: [{ action: 'create', subject: 'Subscription' }],
  add_promoter: [{ action: 'create', subject: 'Promoter' }],
  add_prospect: [{ action: 'create', subject: 'Prospect' }],
  invite_promoter_prospect: [{ action: 'create', subject: 'Promoter' }, { action: 'create', subject: 'Prospect' }],
  create_custom_mini_page: [{ action: 'create', subject: 'Page' }],
  manage_team_members: [{ action: 'manage', subject: 'TeamMember' }],
  edit_permission: [{ action: 'manage', subject: 'Permission' }],
  view_rewards: [{ action: 'read', subject: 'Reward' }],
  view_assigned_promoters: [{ action: 'read', subject: 'Promoter' }],
  edit_promoter: [{ action: 'update', subject: 'Promoter' }],
  delete_promoter: [{ action: 'delete', subject: 'Promoter' }],
  edit_prospect: [{ action: 'update', subject: 'Prospect' }],
  delete_prospect: [{ action: 'delete', subject: 'Prospect' }],
  edit_team_member: [{ action: 'update', subject: 'TeamMember' }],
  delete_team_member: [{ action: 'delete', subject: 'TeamMember' }],
};

function buildAbility(permissions: VendorPermission[]): AppAbility {
  const { can, build } = new AbilityBuilder<AppAbility>(createMongoAbility);
  const added = new Set<string>();
  const permSet = new Set(permissions.map((p) => (typeof p === 'string' ? p.trim() : p)));

  for (const perm of permSet) {
    const mappings = permissionToAbility[perm as VendorPermission];
    if (mappings) {
      for (const { action, subject } of mappings) {
        const key = `${action}:${subject}`;
        if (!added.has(key)) {
          can(action, subject);
          added.add(key);
        }
      }
    }
  }

  return build();
}

interface AbilityProviderProps {
  permissions: VendorPermission[];
  children: React.ReactNode;
}

export const AbilityProvider: React.FC<AbilityProviderProps> = ({ permissions, children }) => {
  const ability = useMemo(() => buildAbility(permissions), [permissions]);
  return (
    <AbilityContext.Provider value={ability}>
      {children}
    </AbilityContext.Provider>
  );
};
